import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { categoryAPI, onboardingAPI, insightAPI, photoAPI, statusAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Lock, Unlock, Sparkles, Loader2, Check, Package, Lightbulb, Clock, FileText, Camera, RefreshCw, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  debloat: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/559174ef494ad1f0666c93a97b367bb3647d81faada2ae730e8921b094fea138.png',
  asymmetry: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/4fc82b76eacba289812d144b8a1a9e41fa8f1e86dd725870f329edf842b8c96e.png',
  coloring: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/0eeee50ce3b71916b765aa9683f50382298e9949d173ae904cee8ba8255a50f3.png',
  ratios: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/05fa82b4d2d73397be7293bcae5ca94148f4293ff3e948d271f96a2018cd77b8.png',
  physique: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/461b1f58ca8deed220fb566f1cac62d2ba1317cd1cbdf44cc5be718d42b0262b.png',
  styling: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/47c66145bf2de875fe849e83474cb7c7f17c98ae43c71830465625366c691712.png',
  status: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/3ed3e498d5f3c557b427ade43b540eacdd35e4331b3ec32d9be27edc9b899ffd.png',
};

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editText, setEditText] = useState('');
  const [editProducts, setEditProducts] = useState('');
  const [parsing, setParsing] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [referenceImage, setReferenceImage] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const statusFileRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadCategory();
  }, [slug]);

  const loadCategory = async () => {
    setLoading(true);
    try {
      const [catRes, insightRes] = await Promise.all([
        categoryAPI.getOne(slug),
        insightAPI.getAll(),
      ]);
      setCategory(catRes.data);
      setInsights(insightRes.data.insights?.filter(i =>
        i.from_category === slug || i.to_category === slug || i.to_category === 'all'
      ) || []);
      
      if (slug === 'ratios' || slug === 'status') {
        const photoRes = await photoAPI.getByCategory(slug);
        setPhotos(photoRes.data.photos || []);
      }
    } catch {
      toast.error('Category not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLock = async () => {
    try {
      const res = await categoryAPI.toggleLock(slug);
      setCategory(prev => ({ ...prev, locked: res.data.locked }));
      toast.success(res.data.locked ? 'Category paused' : 'Category resumed');
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const handleReparse = async () => {
    if (!editText.trim()) { toast.error('Enter some text to parse'); return; }
    setParsing(true);
    try {
      const res = await onboardingAPI.parse(slug, { freeform_text: editText, products_text: editProducts || '' });
      await onboardingAPI.save(slug, { data: res.data.parsed_data });
      setCategory(prev => ({
        ...prev,
        data: prev?.data ? { ...prev.data, ...res.data.parsed_data } : res.data.parsed_data,
      }));
      setEditText('');
      setEditProducts('');
      toast.success('Updated and merged successfully!');
    } catch {
      toast.error('Parsing failed');
    } finally {
      setParsing(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const res = await photoAPI.upload({ image_base64: base64, category: slug, note: 'Progress photo' });
        setPhotos(prev => [{ ...res.data, image_base64: base64 }, ...prev]);
        toast.success('Photo uploaded!');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error('Upload failed');
      setUploading(false);
    }
  };

  const handleGenerateReference = async () => {
    setGeneratingImage(true);
    try {
      const res = await statusAPI.generateReference({ aesthetic: category?.data?.brand_elements?.aesthetic || 'creative lifestyle', platform: 'instagram' });
      setReferenceImage(res.data.image_base64);
      toast.success('Reference image generated!');
    } catch (err) {
      toast.error('Image generation failed. Try again.');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleStatusPhotoSubmit = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        await statusAPI.submitPhoto({ image_base64: base64, category: 'status', note: 'Mimicked reference' });
        toast.success('Photo submitted! Status task complete for today.');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error('Submit failed');
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  if (!category) return null;

  const data = category.data || {};

  return (
    <div className="min-h-screen bg-white pb-8" data-testid={`category-page-${slug}`}>
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-lg mx-auto px-6 h-16 flex items-center justify-between">
          <button
            data-testid="category-back-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600"
            style={{ transition: 'color 0.2s' }}
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <span className="text-xs text-gray-400">{category.locked ? 'Paused' : 'Active'}</span>
              <Switch
                data-testid={`category-lock-switch-${slug}`}
                checked={!category.locked}
                onCheckedChange={handleToggleLock}
                className="scale-75"
              />
              {category.locked ? (
                <Lock className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
              ) : (
                <Unlock className="w-4 h-4 text-green-500" strokeWidth={1.5} />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Title */}
        <div className="mb-6 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-2">
            <img src={CATEGORY_ICONS[slug]} alt={slug} className="w-10 h-10 rounded-xl object-contain bg-gray-50 p-1" />
            <span className="text-xs font-mono text-gray-300 tracking-widest">
              {String(category.order + 1).padStart(2, '0')}
            </span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="category-title">
            {category.name}
          </h1>
          <p className="text-gray-400 mt-1">{category.description}</p>
        </div>

        {category.locked && (
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-6 text-sm text-amber-700 animate-fade-in" data-testid="locked-banner">
            This category is paused. Unlock to receive updates and insights.
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="overview" className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <TabsList className="w-full bg-gray-50 rounded-xl h-11 p-1 mb-6" data-testid="category-tabs">
            <TabsTrigger value="overview" className="flex-1 rounded-lg text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">Overview</TabsTrigger>
            <TabsTrigger value="routine" className="flex-1 rounded-lg text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">Routine</TabsTrigger>
            <TabsTrigger value="products" className="flex-1 rounded-lg text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">Products</TabsTrigger>
            {(slug === 'ratios' || slug === 'status') && (
              <TabsTrigger value="photos" className="flex-1 rounded-lg text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
                {slug === 'status' ? 'Create' : 'Photos'}
              </TabsTrigger>
            )}
            <TabsTrigger value="insights" className="flex-1 rounded-lg text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">Insights</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" data-testid="tab-overview">
            {data && !data.parse_error && !data.raw_text ? (
              <div className="space-y-4">
                {renderOverviewData(slug, data)}
              </div>
            ) : data?.raw_text ? (
              <div className="bg-gray-50 rounded-2xl p-5">
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{data.raw_text}</p>
                <p className="text-xs text-amber-500 mt-3">AI parsing was not completed. You can re-parse below.</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-8 text-center">
                <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-gray-400 mb-1">No data yet</p>
                <p className="text-xs text-gray-300">Paste your information below to get started</p>
              </div>
            )}

            {/* Edit / Re-parse Section */}
            <div className="mt-8 space-y-4">
              <h3 className="text-sm font-medium text-gray-500">Update Information</h3>
              <Textarea
                data-testid="category-edit-text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Paste updated notes for this category..."
                className="min-h-[120px] bg-gray-50 border-transparent rounded-2xl p-5 text-base resize-y focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400"
                style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }}
              />
              <Textarea
                data-testid="category-edit-products"
                value={editProducts}
                onChange={(e) => setEditProducts(e.target.value)}
                placeholder="Products you own (optional)..."
                className="min-h-[80px] bg-gray-50 border-transparent rounded-2xl p-5 text-sm resize-y focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400"
                style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }}
              />
              <Button
                data-testid="category-reparse-btn"
                onClick={handleReparse}
                disabled={parsing || !editText.trim()}
                className="w-full rounded-2xl h-12 bg-purple-50 text-purple-700 hover:bg-purple-100 font-semibold border border-purple-100 disabled:opacity-40 active:scale-[0.98]"
                style={{ transition: 'background-color 0.2s, transform 0.15s' }}
              >
                {parsing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />Parsing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />Parse & Update</>
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Routine Tab */}
          <TabsContent value="routine" data-testid="tab-routine">
            {renderRoutineData(slug, data)}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" data-testid="tab-products">
            {renderProductsData(slug, data, category.products)}
          </TabsContent>

          {/* Photos / Status Create Tab */}
          {slug === 'ratios' && (
            <TabsContent value="photos" data-testid="tab-photos">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Progress Photos</h3>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  <Button data-testid="upload-photo-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading} size="sm" className="rounded-full bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-100">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Camera className="w-4 h-4 mr-1" />}
                    Upload Photo
                  </Button>
                </div>

                {/* 3-Photo Requirement */}
                <div className="bg-purple-50 rounded-2xl p-4 border border-purple-100" data-testid="ratios-photo-guide">
                  <p className="text-sm font-semibold text-purple-700 mb-2">3 Photos Required for Analysis</p>
                  <p className="text-xs text-purple-600/70 mb-3">Submit all 3 angles for an accurate Facial Harmony Score. We suggest taking photos with both hands extended for even muscle stretches.</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Front Facing', angle: 'front' },
                      { label: 'Right Profile', angle: 'right' },
                      { label: 'Left Profile', angle: 'left' },
                    ].map(({ label, angle }) => {
                      const hasPhoto = photos.some(p => (p.note || '').toLowerCase().includes(angle));
                      return (
                        <div key={angle} className={`rounded-xl p-3 text-center border ${hasPhoto ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 border-dashed'}`} data-testid={`photo-slot-${angle}`}>
                          {hasPhoto ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto mb-1" strokeWidth={2} />
                          ) : (
                            <Camera className="w-5 h-5 text-gray-300 mx-auto mb-1" strokeWidth={1.5} />
                          )}
                          <p className={`text-[10px] font-medium ${hasPhoto ? 'text-green-600' : 'text-gray-400'}`}>{label}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {photos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {photos.map(p => (
                      <div key={p.id} className="rounded-xl overflow-hidden border border-gray-100">
                        <img src={`data:image/jpeg;base64,${p.image_base64}`} alt="Progress" className="w-full aspect-square object-cover" />
                        <div className="p-2">
                          <p className="text-[10px] text-gray-400">{new Date(p.created_at).toLocaleDateString()}</p>
                          {p.note && <p className="text-[10px] text-gray-500 mt-0.5">{p.note}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-2xl p-8 text-center">
                    <Camera className="w-8 h-8 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-sm text-gray-400">No photos yet</p>
                    <p className="text-xs text-gray-300">Upload your first progress photo</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {slug === 'status' && (
            <TabsContent value="photos" data-testid="tab-status-create">
              <div className="space-y-6">
                {/* AI Reference Generation */}
                <div>
                  <h3 className="text-sm font-semibold mb-2">Daily Content Challenge</h3>
                  <p className="text-xs text-gray-400 mb-4">Generate an AI reference image, then upload your version mimicking the pose or setting (50% similarity in pose/background).</p>
                  
                  <Button
                    data-testid="generate-reference-btn"
                    onClick={handleGenerateReference}
                    disabled={generatingImage}
                    className="w-full rounded-2xl h-12 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-100 font-semibold mb-4"
                  >
                    {generatingImage ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating reference...</>
                    ) : (
                      <><ImageIcon className="w-4 h-4 mr-2" />{referenceImage ? 'Generate Another' : 'Generate Reference Image'}</>
                    )}
                  </Button>

                  {referenceImage && (
                    <div className="space-y-4 animate-scale-in">
                      <div className="rounded-2xl overflow-hidden border border-gray-100">
                        <img src={`data:image/png;base64,${referenceImage}`} alt="Reference" className="w-full aspect-square object-cover" />
                        <div className="p-3 bg-gray-50">
                          <p className="text-xs text-gray-500 font-medium">Reference to mimic</p>
                          <p className="text-[10px] text-gray-400">Match the pose or background, not the looks</p>
                        </div>
                      </div>
                      
                      <Button
                        data-testid="refresh-reference-btn"
                        onClick={handleGenerateReference}
                        disabled={generatingImage}
                        variant="ghost"
                        className="w-full rounded-xl h-10 text-sm text-gray-500"
                      >
                        <RefreshCw className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                        Refresh reference
                      </Button>

                      <div className="border-t border-gray-100 pt-4">
                        <p className="text-sm font-semibold mb-2">Upload your version</p>
                        <input ref={statusFileRef} type="file" accept="image/*" onChange={handleStatusPhotoSubmit} className="hidden" />
                        <Button
                          data-testid="submit-status-photo-btn"
                          onClick={() => statusFileRef.current?.click()}
                          disabled={uploading}
                          className="w-full rounded-2xl h-12 bg-[#09090b] text-white hover:opacity-90 font-semibold"
                        >
                          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload Mimicked Photo
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Previous submissions */}
                {photos.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Previous Submissions</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {photos.map(p => (
                        <div key={p.id} className="rounded-xl overflow-hidden border border-gray-100">
                          <img src={`data:image/jpeg;base64,${p.image_base64}`} alt="Submission" className="w-full aspect-square object-cover" />
                          <p className="text-[10px] text-gray-400 p-2">{new Date(p.created_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Insights Tab */}
          <TabsContent value="insights" data-testid="tab-insights">
            {insights.length > 0 ? (
              <div className="space-y-3">
                {insights.map((insight, i) => (
                  <div key={i} className="bg-purple-50 rounded-2xl p-5 border border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-purple-600" strokeWidth={1.5} />
                      <span className="text-sm font-semibold text-purple-700">{insight.title}</span>
                      <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${
                        insight.priority === 'high' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
                      }`}>{insight.priority}</span>
                    </div>
                    <p className="text-sm text-purple-600/80 leading-relaxed">{insight.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-8 text-center">
                <Lightbulb className="w-8 h-8 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-gray-400">No cross-category insights yet</p>
                <p className="text-xs text-gray-300 mt-1">Fill in more categories to unlock insights</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// --- Render Helpers ---
function renderOverviewData(slug, data) {
  if (!data || typeof data !== 'object') return <EmptyState />;

  const sections = [];
  Object.entries(data).forEach(([key, value]) => {
    if (key === 'raw_text' || key === 'parse_error' || key === 'error') return;
    if (value === null || value === undefined) return;

    if (typeof value === 'string' && value) {
      sections.push(
        <DataCard key={key} title={formatKey(key)} content={value} />
      );
    } else if (typeof value === 'number') {
      sections.push(
        <DataCard key={key} title={formatKey(key)} content={
          <span className="text-2xl font-bold brand-gradient-text">{value}</span>
        } />
      );
    } else if (Array.isArray(value) && value.length > 0) {
      if (typeof value[0] === 'string') {
        sections.push(
          <DataCard key={key} title={formatKey(key)} content={
            <ul className="space-y-1.5">
              {value.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
                  {item}
                </li>
              ))}
            </ul>
          } />
        );
      } else if (typeof value[0] === 'object') {
        sections.push(
          <DataCard key={key} title={formatKey(key)} content={
            <div className="space-y-3">
              {value.slice(0, 5).map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  {Object.entries(item).map(([k, v]) => (
                    v && <div key={k} className="flex gap-2 text-sm">
                      <span className="text-gray-400 flex-shrink-0 capitalize">{formatKey(k)}:</span>
                      <span className="text-gray-700">{String(v)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          } />
        );
      }
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      sections.push(
        <DataCard key={key} title={formatKey(key)} content={
          <div className="space-y-1.5">
            {Object.entries(value).map(([k, v]) => {
              if (!v || (Array.isArray(v) && v.length === 0)) return null;
              return (
                <div key={k} className="flex gap-2 text-sm">
                  <span className="text-gray-400 capitalize flex-shrink-0">{formatKey(k)}:</span>
                  <span className="text-gray-700">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                </div>
              );
            })}
          </div>
        } />
      );
    }
  });

  return sections.length > 0 ? sections : <EmptyState />;
}

function renderRoutineData(slug, data) {
  if (!data || typeof data !== 'object') return <EmptyState label="No routine data" />;

  const routineKeys = ['daily_schedule', 'exercises', 'workout_plan', 'outfit_formulas', 'platforms'];
  const routineData = routineKeys.reduce((acc, key) => {
    if (data[key]) acc[key] = data[key];
    return acc;
  }, {});

  if (Object.keys(routineData).length === 0) {
    // Check for timeline
    if (data.timeline) {
      return (
        <DataCard title="Timeline" content={
          <div className="space-y-2">
            {Object.entries(data.timeline).map(([k, v]) => (
              v && <div key={k} className="flex gap-2 text-sm">
                <Clock className="w-3.5 h-3.5 text-purple-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-gray-400 capitalize">{formatKey(k)}:</span>
                <span className="text-gray-700">{String(v)}</span>
              </div>
            ))}
          </div>
        } />
      );
    }
    return <EmptyState label="No routine data yet" />;
  }

  return (
    <div className="space-y-4">
      {Object.entries(routineData).map(([key, value]) => (
        <DataCard key={key} title={formatKey(key)} content={
          Array.isArray(value) ? (
            <div className="space-y-3">
              {value.map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  {typeof item === 'string' ? (
                    <span className="text-sm text-gray-700">{item}</span>
                  ) : (
                    Object.entries(item).map(([k, v]) => {
                      if (!v || (Array.isArray(v) && v.length === 0)) return null;
                      return (
                        <div key={k} className="flex gap-2 text-sm mb-1">
                          <span className="text-gray-400 capitalize flex-shrink-0">{formatKey(k)}:</span>
                          <span className="text-gray-700">{Array.isArray(v) ? v.map((ex, idx) => (
                            typeof ex === 'object' ? <span key={idx} className="block">{Object.values(ex).filter(Boolean).join(' - ')}</span> : ex
                          )) : String(v)}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>
          ) : typeof value === 'object' ? (
            <div className="space-y-1.5">
              {Object.entries(value).map(([k, v]) => (
                v && <div key={k} className="text-sm">
                  <span className="text-gray-400 capitalize">{formatKey(k)}:</span>{' '}
                  <span className="text-gray-700">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-700">{String(value)}</p>
          )
        } />
      ))}
    </div>
  );
}

function renderProductsData(slug, data, products) {
  const allProducts = [];

  // From parsed data
  if (data?.supplements) allProducts.push(...data.supplements.map(s => ({ name: s.name, detail: `${s.dose} - ${s.timing}` })));
  if (data?.skin?.products) allProducts.push(...data.skin.products.map(p => ({ name: p, detail: 'Skincare' })));
  if (data?.shopping_list) allProducts.push(...data.shopping_list.map(p => ({ name: p, detail: 'To buy' })));

  // From saved products
  if (products?.length) allProducts.push(...products.map(p => ({ name: p.name || p, detail: p.description || '' })));

  if (allProducts.length === 0) {
    return (
      <div className="bg-gray-50 rounded-2xl p-8 text-center">
        <Package className="w-8 h-8 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-sm text-gray-400">No products tracked</p>
        <p className="text-xs text-gray-300 mt-1">Add products during onboarding or update your info</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {allProducts.map((p, i) => (
        <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
          <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
            <Package className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">{p.name}</p>
            {p.detail && <p className="text-xs text-gray-400">{p.detail}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function DataCard({ title, content }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
      <h4 className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-3">{title}</h4>
      <div>{typeof content === 'string' ? <p className="text-sm text-gray-700 leading-relaxed">{content}</p> : content}</div>
    </div>
  );
}

function EmptyState({ label = 'No data yet' }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-8 text-center">
      <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}

function formatKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}
